import { ReactElement, ReactNode, isValidElement } from 'react'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Tooltip, TooltipProps, Typography, TypographyProps } from '@mui/material'

export type SwitchInfoTooltipProps = Omit<TooltipProps, 'children' | 'title'> & {
  title: ReactNode
  titleTypographyProps?: TypographyProps
}

const renderTitle = (title: ReactNode, titleTypographyProps?: TypographyProps): ReactNode => {
  if (isValidElement(title)) return title

  return (
    <Typography
      variant="body2"
      sx={{ whiteSpace: 'pre-line', color: 'inherit' }}
      {...titleTypographyProps}
    >
      {title}
    </Typography>
  )
}

const SwitchInfoTooltip = ({
  placement = 'left',
  title,
  titleTypographyProps,
  slotProps,
  ...tooltipProps
}: SwitchInfoTooltipProps): ReactElement => {
  const tooltipSlotProps =
    slotProps?.tooltip && typeof slotProps.tooltip === 'object' ? slotProps.tooltip : {}
  const arrowSlotProps =
    slotProps?.arrow && typeof slotProps.arrow === 'object' ? slotProps.arrow : {}

  return (
    <Tooltip
      placement={placement}
      arrow
      title={renderTitle(title, titleTypographyProps)}
      slotProps={{
        ...slotProps,
        tooltip: {
          ...tooltipSlotProps,
          sx: theme => ({
            bgcolor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            boxShadow: theme.shadows[4],
            borderRadius: theme.shape.borderRadius,
            px: 1,
            py: 1,
          }),
        },
        arrow: {
          ...arrowSlotProps,
          sx: theme => ({
            color: theme.palette.background.paper,
          }),
        },
        popper: {
          sx: { padding: '30px' },
        },
      }}
      {...tooltipProps}
    >
      <InfoOutlinedIcon
        fontSize="small"
        sx={({ palette }) => ({
          color: palette.info.main,
          cursor: 'help',
          display: 'flex',
        })}
      />
    </Tooltip>
  )
}

export default SwitchInfoTooltip
